import React, { useEffect, useMemo, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompetition } from "@/context/CompetitionContext";
import CompetitionSelector from "@/components/CompetitionSelector";
import { DollarSign, RotateCcw, X, Trash2, Mail, Phone, Info } from "lucide-react";

const brl = (n) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    n || 0
  );

const STATUS = {
  pending: {
    label: "Aguardando pagamento",
    cls: "bg-[#FFCC00]/20 text-[#FFCC00] border-[#FFCC00]/40",
  },
  paid: {
    label: "PAGA · inscrito",
    cls: "bg-[#5AF78E]/20 text-[#5AF78E] border-[#5AF78E]/40",
  },
  rejected: {
    label: "Rejeitada",
    cls: "bg-[#C9A84C]/20 text-[#C9A84C] border-[#C9A84C]/40",
  },
};

export default function Registrations() {
  const { user } = useAuth();
  const canDelete = user?.role === "admin";
  const { currentId } = useCompetition();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    if (!currentId) return;
    setError("");
    try {
      const [r, c] = await Promise.all([
        api.get(`/competitions/${currentId}/registrations`),
        api.get(`/competitions/${currentId}/categories`),
      ]);
      setItems(r.data);
      setCats(c.data);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  useEffect(() => {
    load();
     
  }, [currentId]);

  const catMap = useMemo(
    () => Object.fromEntries(cats.map((c) => [c.id, c.nome])),
    [cats]
  );

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((r) => r.status === filter);
  }, [items, filter]);

  const totals = useMemo(() => {
    const pending = items.filter((r) => r.status === "pending").length;
    const paid = items.filter((r) => r.status === "paid").length;
    const receita = items
      .filter((r) => r.status === "paid")
      .reduce((sum, r) => sum + (r.valor_total || 0), 0);
    const aguardando = items
      .filter((r) => r.status === "pending")
      .reduce((sum, r) => sum + (r.valor_total || 0), 0);
    return { pending, paid, receita, aguardando };
  }, [items]);

  const porCategoria = useMemo(() => {
    // Only officially inscritos (paid) count in the tally
    const paid = items.filter((r) => r.status === "paid");
    const map = new Map();
    for (const r of paid) {
      const bump = (catId, isDobra) => {
        const cur = map.get(catId) || { total: 0, dobras: 0 };
        cur.total += 1;
        if (isDobra) cur.dobras += 1;
        map.set(catId, cur);
      };
      bump(r.category_id, false);
      const dobras =
        r.dobra_category_ids && r.dobra_category_ids.length > 0
          ? r.dobra_category_ids
          : r.dobra_category_id
          ? [r.dobra_category_id]
          : [];
      for (const d of dobras) bump(d, true);
    }
    return cats
      .map((c) => ({
        ...c,
        total: map.get(c.id)?.total || 0,
        dobras: map.get(c.id)?.dobras || 0,
      }))
      .filter((c) => c.total > 0);
  }, [items, cats]);

  const markPaid = async (rid) => {
    setBusy(rid);
    try {
      await api.post(`/registrations/${rid}/mark-paid`);
      await load();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
    setBusy(null);
  };

  const markUnpaid = async (rid) => {
    if (
      !window.confirm(
        "Reverter para pendente? Isso remove os atletas oficialmente criados."
      )
    )
      return;
    setBusy(rid);
    try {
      await api.post(`/registrations/${rid}/mark-unpaid`);
      await load();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
    setBusy(null);
  };

  const reject = async (rid) => {
    if (!window.confirm("Rejeitar inscrição? Se já foi aprovada, os atletas criados serão removidos."))
      return;
    setBusy(rid);
    try {
      await api.post(`/registrations/${rid}/reject`);
      await load();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
    setBusy(null);
  };

  const del = async (rid) => {
    if (!window.confirm("Excluir inscrição definitivamente?")) return;
    setBusy(rid);
    try {
      await api.delete(`/registrations/${rid}`);
      await load();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
    setBusy(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">Área do Atleta</div>
          <h1 className="font-display text-5xl font-black tracking-tight">
            PAGAMENTOS
          </h1>
        </div>
        <CompetitionSelector />
      </div>

      <div
        data-testid="payments-info-banner"
        className="card p-4 mb-6 flex items-start gap-3 border-l-4 border-[#C9A84C]"
      >
        <Info size={20} className="text-[#C9A84C] shrink-0 mt-0.5" />
        <div className="text-sm">
          <b>Fluxo oficial:</b> Toda inscrição fica{" "}
          <span className="text-[#FFCC00]">AGUARDANDO PAGAMENTO</span> até você
          clicar em <b>MARCAR COMO PAGA</b>. Apenas nesse momento o atleta é
          oficialmente inscrito nas categorias escolhidas (principal + dobra
          quando aplicável) e passa a constar no telão, súmulas e ranking.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="stat-card" data-testid="stat-pending">
          <div className="overline mb-2">Aguardando</div>
          <div className="stat-number text-[#FFCC00]">{totals.pending}</div>
          <div className="text-xs text-[var(--text-secondary)] tabular-nums mt-1">
            {brl(totals.aguardando)}
          </div>
        </div>
        <div className="stat-card" data-testid="stat-paid">
          <div className="overline mb-2">Pagas</div>
          <div className="stat-number text-[#5AF78E]">{totals.paid}</div>
          <div className="text-xs text-[var(--text-secondary)] mt-1">
            atletas inscritos
          </div>
        </div>
        <div className="stat-card md:col-span-2" data-testid="stat-receita">
          <div className="overline mb-2">Receita Confirmada</div>
          <div className="stat-number text-white tabular-nums">
            {brl(totals.receita)}
          </div>
        </div>
      </div>

      {porCategoria.length > 0 && (
        <div className="card p-5 mb-6" data-testid="por-categoria-box">
          <div className="overline mb-3">Inscritos por Categoria (pagas)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {porCategoria.map((c) => (
              <div
                key={c.id}
                data-testid={`cat-summary-${c.id}`}
                className="flex items-center justify-between border-b border-white/5 py-1.5 text-sm"
              >
                <span>{c.nome}</span>
                <span className="tabular-nums font-display font-bold text-lg">
                  {c.total}
                  {c.dobras > 0 && (
                    <span className="ml-1 text-[10px] text-[#C9A84C] align-super">
                      ({c.dobras} dobra{c.dobras > 1 ? "s" : ""})
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        {[
          { k: "all", label: "Todas" },
          { k: "pending", label: "Aguardando" },
          { k: "paid", label: "Pagas" },
          { k: "rejected", label: "Rejeitadas" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`filter-${t.k}`}
            className={
              "btn-ghost " + (filter === t.k ? "!bg-[#C9A84C] !text-[#0A0A0A] !border-[#C9A84C]" : "")
            }
            onClick={() => setFilter(t.k)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="text-sm text-[#C9A84C] border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-3 py-2 mb-4">
          {error}
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="table-dense">
          <thead>
            <tr>
              <th>Atleta</th>
              <th>Categorias</th>
              <th>Lote</th>
              <th>Valor</th>
              <th>Status</th>
              <th className="text-right pr-4">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const s = STATUS[r.status] || STATUS.pending;
              return (
                <tr key={r.id} data-testid={`reg-row-${r.id}`}>
                  <td>
                    <div className="font-semibold">{r.nome}</div>
                    <div className="text-xs text-[var(--text-secondary)] flex flex-wrap gap-3 mt-1">
                      <span className="flex items-center gap-1">
                        <Mail size={11} /> {r.email}
                      </span>
                      {r.telefone && (
                        <span className="flex items-center gap-1">
                          <Phone size={11} /> {r.telefone}
                        </span>
                      )}
                      {r.equipe && <span>· {r.equipe}</span>}
                    </div>
                  </td>
                  <td>
                    <div>{catMap[r.category_id] || "—"}</div>
                    {(r.dobra_category_ids && r.dobra_category_ids.length > 0
                      ? r.dobra_category_ids
                      : r.dobra_category_id
                      ? [r.dobra_category_id]
                      : []
                    ).map((d, i) => (
                      <div className="mt-1" key={d || `empty-${i}`}>
                        <span
                          className="chip chip-admin"
                          style={{ background: "#C9A84C", color: "#0A0A0A" }}
                          data-testid={`reg-dobra-tag-${r.id}-${i}`}
                        >
                          DOBRA {i + 1}
                        </span>{" "}
                        <span className="text-xs text-[var(--text-secondary)]">
                          {catMap[d] || d}
                        </span>
                      </div>
                    ))}
                  </td>
                  <td className="text-xs">{r.lote_nome || "—"}</td>
                  <td className="tabular-nums font-display font-bold">
                    {brl(r.valor_total)}
                  </td>
                  <td>
                    <span
                      className={"chip border px-2 py-0.5 text-xs " + s.cls}
                      data-testid={`reg-status-${r.id}`}
                    >
                      {s.label}
                    </span>
                  </td>
                  <td className="text-right pr-4">
                    {r.status === "pending" && (
                      <button
                        data-testid={`reg-mark-paid-${r.id}`}
                        className="btn-primary mr-1 !py-1 !px-3 !text-xs inline-flex items-center gap-1"
                        disabled={busy === r.id}
                        onClick={() => markPaid(r.id)}
                        title="Marcar como Paga"
                      >
                        <DollarSign size={13} /> MARCAR COMO PAGA
                      </button>
                    )}
                    {r.status === "paid" && (
                      <button
                        data-testid={`reg-mark-unpaid-${r.id}`}
                        className="btn-ghost mr-1 !text-[#FFCC00]"
                        disabled={busy === r.id}
                        onClick={() => markUnpaid(r.id)}
                        title="Reverter pagamento (remove atletas)"
                      >
                        <RotateCcw size={14} />
                      </button>
                    )}
                    {r.status !== "rejected" && (
                      <button
                        data-testid={`reg-reject-${r.id}`}
                        className="btn-ghost mr-1 !text-[#FFCC00]"
                        disabled={busy === r.id}
                        onClick={() => reject(r.id)}
                        title="Rejeitar"
                      >
                        <X size={14} />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        data-testid={`reg-delete-${r.id}`}
                        className="btn-ghost !text-[#C9A84C]"
                        disabled={busy === r.id}
                        onClick={() => del(r.id)}
                        title="Excluir"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="text-center py-8 text-[var(--text-secondary)]"
                >
                  Nenhuma inscrição para exibir.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
